/**
 * useLecturerEvaluationController
 *
 * Encapsulates the logic for saving lecturer evaluations and feedback.
 * Handles serialization, AbortController, and status management.
 */

import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { useUpdateEvaluation } from '../../../common/api/endpoints/submission-evaluation/submission-evaluation';
import { updateEvaluationBody } from '../../../common/api/endpoints/submission-evaluation/submission-evaluation.zod';
import { getGetTasksByUserCourseModuleQueryKey } from '../../../common/api/endpoints/tasks/tasks';
import type { GenericApiResponseListTask } from '../../../common/api/models/genericApiResponseListTask';
import type { FlatTaskEvaluationInput } from '../../../common/api/models/flatTaskEvaluationInput';
import type { OralExamTaskEvaluationInput } from '../../../common/api/models/oralExamTaskEvaluationInput';
import log from '../../../common/utils/logger';
import type { LecturerEvaluationUpdateRequest } from '../../../common/api/models/lecturerEvaluationUpdateRequest';
import type { TaskFormEvaluation } from '../types';
import {
  formTaskEvaluationToAutosavePayload,
} from '../utils/evaluationFormMapping';
import { useEvaluationGradingConfig } from '../context/EvaluationGradingConfigContext';
import { getFlatEvaluationDimensions } from '../../../common/utils/evaluation/getFlatEvaluationDimensions';

function mergeSavedTaskEvaluationIntoTasksCache(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
  courseId: string,
  moduleId: string,
  taskId: string,
  formEval: TaskFormEvaluation,
  serialized: LecturerEvaluationUpdateRequest,
) {
  const queryKey = getGetTasksByUserCourseModuleQueryKey(
    userId,
    courseId,
    moduleId,
    { include: 'submissions' },
  );
  const cached = queryClient.getQueryData<AxiosResponse<GenericApiResponseListTask>>(queryKey);
  if (!cached?.data?.data) {
    return;
  }

  queryClient.setQueryData<AxiosResponse<GenericApiResponseListTask>>(queryKey, {
    ...cached,
    data: {
      ...cached.data,
      data: cached.data.data.map((task) => {
        if (task.id !== taskId || !task.submission) {
          return task;
        }

        const nextSubmission = { ...task.submission };

        if (serialized.feedback) {
          nextSubmission.feedback = {
            ...nextSubmission.feedback,
            ...serialized.feedback,
          };
        }

        if (serialized.evaluation) {
          if ('kind' in serialized.evaluation && serialized.evaluation.kind === 'oral_exam') {
            nextSubmission.evaluation = serialized.evaluation as OralExamTaskEvaluationInput;
          } else if ('kind' in serialized.evaluation && serialized.evaluation.kind === 'flat') {
            const flatEval = serialized.evaluation as FlatTaskEvaluationInput;
            const existingDims = getFlatEvaluationDimensions(task.submission.evaluation);
            const mergedDims = {
              ...existingDims,
              ...(flatEval.dimensions ?? {}),
            };
            nextSubmission.evaluation = {
              kind: 'flat',
              dimensions: mergedDims,
            };
          }
        } else if (formEval.kind === 'flat') {
          const existingDims = getFlatEvaluationDimensions(task.submission.evaluation);
          const mergedDims = { ...existingDims };
          Object.entries(formEval.evaluation?.dimensions ?? {}).forEach(([dim, rating]) => {
            mergedDims[dim] = {
              level: rating?.level ?? null,
              comment: rating?.comment ?? null,
            };
          });
          nextSubmission.evaluation = {
            kind: 'flat',
            dimensions: mergedDims,
          };
        }

        return {
          ...task,
          submission: nextSubmission,
        };
      }),
    },
  });
}

export type EvaluationSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseLecturerEvaluationControllerArgs {
  evaluationUserId: string;
  evaluationCourseId: string;
  evaluationModuleId: string;
  taskId: string;
  applicableDims: string[];
}

export const useLecturerEvaluationController = ({
  evaluationUserId,
  evaluationCourseId,
  evaluationModuleId,
  taskId,
  applicableDims,
}: UseLecturerEvaluationControllerArgs) => {
  const [status, setStatus] = useState<EvaluationSaveStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const updateEvaluationMutation = useUpdateEvaluation();
  const abortControllerRef = useRef<AbortController | null>(null);
  const {
    evalDimensionKeys,
    oralExamDimensionKeys,
    systemManagedKeys,
  } = useEvaluationGradingConfig();

  const saveEvaluation = useCallback(async (formData: TaskFormEvaluation | Record<string, unknown>) => {
    // 1. Abort any stale request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setStatus('saving');
      setErrorMessage(null);

      const taskFormEval = formData as TaskFormEvaluation;
      const serialized = formTaskEvaluationToAutosavePayload(
        taskFormEval,
        evalDimensionKeys,
        oralExamDimensionKeys,
        applicableDims,
        systemManagedKeys,
      );

      const payload = {
        userId: evaluationUserId,
        courseId: evaluationCourseId,
        moduleId: evaluationModuleId,
        taskId,
        data: serialized as unknown as LecturerEvaluationUpdateRequest,
      };

      // Validate payload
      updateEvaluationBody.parse(payload.data);

      await updateEvaluationMutation.mutateAsync(payload);

      mergeSavedTaskEvaluationIntoTasksCache(
        queryClient,
        evaluationUserId,
        evaluationCourseId,
        evaluationModuleId,
        taskId,
        taskFormEval,
        serialized as unknown as LecturerEvaluationUpdateRequest,
      );

      if (!abortControllerRef.current.signal.aborted) {
        setStatus('saved');
        // Auto-reset 'saved' status to 'idle' after a delay
        setTimeout(() => {
          setStatus((prev) => (prev === 'saved' ? 'idle' : prev));
        }, 3000);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      log.error('[LecturerController] Error saving evaluation:', error);
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Save failed');
      throw error;
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        abortControllerRef.current = null;
      }
    }
  }, [
    evaluationUserId,
    evaluationCourseId,
    evaluationModuleId,
    taskId,
    applicableDims,
    evalDimensionKeys,
    oralExamDimensionKeys,
    systemManagedKeys,
    updateEvaluationMutation,
    queryClient,
  ]);

  return {
    saveEvaluation,
    status,
    errorMessage,
  };
};
